import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Tag,
  Button,
  Spin,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Space,
  Popconfirm,
  Dropdown,
} from 'antd';
import {
  ArrowLeftOutlined,
  WifiOutlined,
  CloudDownloadOutlined,
  ThunderboltOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ApiClient } from '../../services/apiClient';
import { simOperation } from '../../services/adminService';
import { useAuth } from '../../auth/AuthContext';
import PageHelp from '../../components/PageHelp';
import EsimQrModal from '../../components/EsimQrModal';
import EsimBundleCard from '../../components/esim/EsimBundleCard';

interface SimCardDetail {
  id: number;
  iccid: string;
  msisdn: string;
  imsi: string | null;
  eid: string | null;
  status: string;
  alias1: string | null;
  alias2: string | null;
  alias3: string | null;
  alias4: string | null;
  product: string | null;
  service_product: string | null;
  subscription_profile: string | null;
  roaming_control: string | null;
  activation_ready_date: string | null;
  ready_validity_end_date: string | null;
  activation_date: string | null;
  cooldown_date: string | null;
  deactivation_date: string | null;
  data_warning: string | null;
  data_suspend: string | null;
  data_warning_status: string | null;
  data_suspend_status: string | null;
  sms_mo_warning: string | null;
  sms_mo_suspend: string | null;
  voice_mo_warning: string | null;
  voice_mo_suspend: string | null;
  order_id: string | null;
  inventory: string | null;
  rsp_state: string | null;
  custom_reference: string | null;
  pin1: string | null;
  puk1: string | null;
  created_at: string;
  updated_at: string;
}

interface UsageRecord {
  id: number;
  usage_date: string;
  data_volume_bytes: number;
  data_volume_mb: string;
  data_volume_gb: string;
  voice_duration_seconds: number;
  voice_call_count: number;
  sms_mo_count: number;
  sms_mt_count: number;
}

interface UsageSummary {
  iccid: string;
  msisdn: string;
  alias1: string | null;
  status: string;
  days_with_usage: number;
  total_data_bytes: string;
  total_data_mb: string;
  total_data_gb: string;
  first_usage: string;
  last_usage: string;
}

type Period = '7d' | '30d' | '90d' | 'all';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
};

const statusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'green';
    case 'suspended':
      return 'orange';
    case 'deactivated':
    case 'terminated':
      return 'red';
    case 'ready':
      return 'blue';
    default:
      return 'default';
  }
};

const SimDetail: React.FC = () => {
  const { provider, identifier } = useParams<{ provider: string; identifier: string }>();
  const navigate = useNavigate();
  const { role: myRole } = useAuth();
  const canOperate = myRole === 'super_admin' || myRole === 'reseller_admin';
  const [sim, setSim] = useState<SimCardDetail | null>(null);
  const [usage, setUsage] = useState<UsageRecord[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [period, setPeriod] = useState<Period>('30d');
  const [operating, setOperating] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const fetchSim = async () => {
    setLoading(true);
    try {
      if (provider === 'vrs') {
        const data = await ApiClient.get<SimCardDetail>(
          `/vrs/sim-cards/${identifier}?refresh=true`
        );
        setSim(data);
        const summaryRes = await ApiClient.get<{ data: UsageSummary[] }>(
          `/vrs/usage/summary?iccid=${identifier}`
        );
        if (summaryRes.data?.length > 0) {
          setSummary(summaryRes.data[0]);
        }
      }
    } catch (err: any) {
      message.error(err.message || 'Failed to load SIM details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSim();
    // eslint-disable-next-line
  }, [provider, identifier]);

  const handleOperation = async (op: 'activate' | 'suspend' | 'resume') => {
    if (!provider || !identifier) return;
    setOperating(true);
    try {
      await simOperation(provider, identifier, op);
      message.success(`SIM ${op} successful`);
      fetchSim(); // Refresh after operation
    } catch (err: any) {
      message.error(err.message || `Failed to ${op} SIM`);
    } finally {
      setOperating(false);
    }
  };

  useEffect(() => {
    const fetchUsage = async () => {
      if (provider !== 'vrs' || !identifier) return;
      setUsageLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('iccid', identifier!);
        params.set('limit', '500');
        if (period !== 'all') {
          const now = new Date();
          const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
          const start = new Date(now.getTime() - days * 86400000);
          params.set('startDate', start.toISOString().split('T')[0]);
          params.set('endDate', now.toISOString().split('T')[0]);
        }
        const res = await ApiClient.get<{ data: UsageRecord[] }>(`/vrs/usage?${params}`);
        setUsage(res.data || []);
      } catch {
        setUsage([]);
      } finally {
        setUsageLoading(false);
      }
    };
    fetchUsage();
  }, [provider, identifier, period]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!sim) {
    return (
      <div style={{ padding: 24 }}>
        <p>SIM not found.</p>
        <Button onClick={() => navigate('/admin/sims')}>Back to Inventory</Button>
      </div>
    );
  }

  const chartData = [...usage]
    .sort((a, b) => a.usage_date.localeCompare(b.usage_date))
    .map(r => ({
      date: new Date(r.usage_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      dataGB: parseFloat(r.data_volume_gb) || 0,
      dataBytes: r.data_volume_bytes,
    }));

  const totalDataBytes = usage.reduce((sum, r) => sum + (r.data_volume_bytes || 0), 0);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/sims')}
          style={{ paddingLeft: 0 }}
        >
          Back to Inventory
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 4,
        }}
      >
        <div>
          <h2 style={{ marginBottom: 4 }}>
            {sim.alias1 || sim.iccid} <Tag color={statusColor(sim.status)}>{sim.status}</Tag>{' '}
            <PageHelp
              title="SIM Details Help"
              sections={[
                {
                  title: 'SIM Information',
                  content:
                    'Complete details from the Data carrier including identifiers (ICCID, MSISDN, IMSI), product type, roaming profile, and subscription info.',
                },
                {
                  title: 'Usage Data',
                  content:
                    'The chart shows daily data consumption. Use the period selector to view 7 days, 30 days, 90 days, or all time.\n\nThe table below shows individual daily records with data volume, voice calls, and SMS counts.',
                },
                {
                  title: 'Actions',
                  content:
                    '- Refresh: reload SIM data from Data carrier\n- Suspend: temporarily pause connectivity\n- Resume: reactivate a suspended SIM\n- Activate: activate a SIM that is ready\n\nOnly Reseller Admin and Super Admin can perform these actions.',
                  tips: [
                    'Suspend/Resume take effect within minutes',
                    'Termination is permanent \u2014 use with extreme caution',
                  ],
                },
                {
                  title: 'Thresholds',
                  content:
                    'Data and SMS warning/suspend thresholds set on the carrier. When usage exceeds the warning level, an alert is triggered. At the suspend level, the SIM is automatically suspended.',
                },
                {
                  title: 'Bundle & remaining data (Travel eSIMs)',
                  content:
                    'Remaining data, expiry and the balance type (what happens when the allowance is used up) come live from Vodafone. "Top up" adds a bundle: same coverage merges the data, a different coverage stacks a second bundle (max three). Throttled bundles cannot be topped up.\n\nThe full explanation is in Administration → eSIM Guide.',
                  tips: [
                    'Vodafone allows one balance query per minute per eSIM — Refresh may show the last known value briefly',
                    'Validity starts on first use, so a fresh eSIM shows no expiry yet',
                  ],
                },
              ]}
            />
          </h2>
          {sim.alias1 && (
            <div style={{ color: '#888', marginBottom: 16, fontSize: 13 }}>ICCID: {sim.iccid}</div>
          )}
        </div>
        {canOperate && provider === 'vrs' && (
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSim} loading={loading} size="small">
              Refresh
            </Button>
            {(sim.eid || sim.subscription_profile === 'Travel eSIM') && (
              <Button icon={<QrcodeOutlined />} size="small" onClick={() => setQrOpen(true)}>
                Show activation QR
              </Button>
            )}
            {sim.status === 'active' && (
              <Popconfirm title="Suspend this SIM?" onConfirm={() => handleOperation('suspend')}>
                <Button
                  icon={<PauseCircleOutlined />}
                  loading={operating}
                  size="small"
                  style={{ color: '#faad14' }}
                >
                  Suspend
                </Button>
              </Popconfirm>
            )}
            {sim.status === 'suspended' && (
              <Popconfirm title="Resume this SIM?" onConfirm={() => handleOperation('resume')}>
                <Button
                  icon={<PlayCircleOutlined />}
                  loading={operating}
                  size="small"
                  type="primary"
                >
                  Resume
                </Button>
              </Popconfirm>
            )}
            {sim.status !== 'active' && sim.status !== 'suspended' && (
              <Popconfirm title="Activate this SIM?" onConfirm={() => handleOperation('activate')}>
                <Button
                  icon={<ThunderboltOutlined />}
                  loading={operating}
                  size="small"
                  type="primary"
                >
                  Activate
                </Button>
              </Popconfirm>
            )}
          </Space>
        )}
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Data (All Time)"
              value={summary ? parseFloat(summary.total_data_gb).toFixed(2) : '—'}
              suffix={summary ? 'GB' : ''}
              prefix={<CloudDownloadOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Days with Usage"
              value={summary?.days_with_usage ?? '—'}
              prefix={<WifiOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="First Usage"
              value={
                summary?.first_usage
                  ? new Date(summary.first_usage).toLocaleDateString('en-GB')
                  : '—'
              }
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Last Usage"
              value={
                summary?.last_usage ? new Date(summary.last_usage).toLocaleDateString('en-GB') : '—'
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Usage Chart */}
      <Card
        title="Data Usage"
        style={{ marginBottom: 24 }}
        extra={
          <Select value={period} onChange={setPeriod} style={{ width: 130 }} size="small">
            <Select.Option value="7d">Last 7 days</Select.Option>
            <Select.Option value="30d">Last 30 days</Select.Option>
            <Select.Option value="90d">Last 90 days</Select.Option>
            <Select.Option value="all">All time</Select.Option>
          </Select>
        }
      >
        {usageLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : chartData.length > 0 ? (
          <>
            <div style={{ marginBottom: 8, color: '#888', fontSize: 13 }}>
              Period total: {formatBytes(totalDataBytes)}
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  label={{ value: 'GB', angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <Tooltip formatter={(val: number) => [`${val.toFixed(2)} GB`, 'Data']} />
                <Bar dataKey="dataGB" fill="#1890ff" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            No usage data for this period
          </div>
        )}
      </Card>

      {/* KAN-167: Travel eSIMs carry a prepaid bundle — show its live balance. */}
      {provider === 'vrs' && sim.subscription_profile === 'Travel eSIM' && (
        <EsimBundleCard iccid={sim.iccid} canOperate={canOperate} />
      )}

      {/* SIM Details */}
      <Card title="SIM Information" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="ICCID">{sim.iccid}</Descriptions.Item>
          <Descriptions.Item label="MSISDN">{sim.msisdn || '—'}</Descriptions.Item>
          <Descriptions.Item label="IMSI">{sim.imsi || '—'}</Descriptions.Item>
          <Descriptions.Item label="EID">{sim.eid || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusColor(sim.status)}>{sim.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Product">{sim.product || '—'}</Descriptions.Item>
          <Descriptions.Item label="Service Product">
            {sim.service_product || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Subscription Profile">
            {sim.subscription_profile || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Roaming Control">
            {sim.roaming_control || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Order ID">{sim.order_id || '—'}</Descriptions.Item>
          <Descriptions.Item label="Custom Reference">
            {sim.custom_reference || '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Aliases */}
      <Card title="Aliases" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Alias 1">{sim.alias1 || '—'}</Descriptions.Item>
          <Descriptions.Item label="Alias 2">{sim.alias2 || '—'}</Descriptions.Item>
          <Descriptions.Item label="Alias 3">{sim.alias3 || '—'}</Descriptions.Item>
          <Descriptions.Item label="Alias 4">{sim.alias4 || '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Dates & Lifecycle */}
      <Card title="Lifecycle" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Activation Ready">
            {sim.activation_ready_date ? new Date(sim.activation_ready_date).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Activation Date">
            {sim.activation_date ? new Date(sim.activation_date).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Cooldown Date">
            {sim.cooldown_date ? new Date(sim.cooldown_date).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Deactivation Date">
            {sim.deactivation_date ? new Date(sim.deactivation_date).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="RSP State">{sim.rsp_state || '—'}</Descriptions.Item>
          <Descriptions.Item label="Inventory">{sim.inventory || '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Thresholds */}
      <Card title="Thresholds" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Data Warning">{sim.data_warning || '—'}</Descriptions.Item>
          <Descriptions.Item label="Data Suspend">{sim.data_suspend || '—'}</Descriptions.Item>
          <Descriptions.Item label="Data Warning Status">
            {sim.data_warning_status || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Data Suspend Status">
            {sim.data_suspend_status || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="SMS MO Warning">{sim.sms_mo_warning || '—'}</Descriptions.Item>
          <Descriptions.Item label="SMS MO Suspend">{sim.sms_mo_suspend || '—'}</Descriptions.Item>
          <Descriptions.Item label="Voice MO Warning">
            {sim.voice_mo_warning || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Voice MO Suspend">
            {sim.voice_mo_suspend || '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Usage Table */}
      {usage.length > 0 && (
        <Card title="Daily Usage Records">
          <Table
            dataSource={[...usage].sort((a, b) => b.usage_date.localeCompare(a.usage_date))}
            rowKey="id"
            size="small"
            pagination={{
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20', '50', '100'],
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            columns={[
              {
                title: 'Date',
                dataIndex: 'usage_date',
                render: (d: string) => new Date(d).toLocaleDateString('en-GB'),
              },
              {
                title: 'Data',
                dataIndex: 'data_volume_bytes',
                render: (b: number) => formatBytes(b),
                sorter: (a: UsageRecord, b: UsageRecord) =>
                  a.data_volume_bytes - b.data_volume_bytes,
              },
              { title: 'Voice Calls', dataIndex: 'voice_call_count' },
              {
                title: 'Voice Duration',
                dataIndex: 'voice_duration_seconds',
                render: (s: number) => (s > 0 ? `${Math.floor(s / 60)}m ${s % 60}s` : '—'),
              },
              { title: 'SMS Sent', dataIndex: 'sms_mo_count' },
              { title: 'SMS Received', dataIndex: 'sms_mt_count' },
            ]}
          />
        </Card>
      )}
      <EsimQrModal iccid={sim.iccid} open={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

export default SimDetail;
